
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { createTemplate } from "@/services/templateService";
import { Template } from "@/types/whatsApp";
import { useToast } from "@/components/ui/use-toast";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const TemplateForm = () => {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [variableInput, setVariableInput] = useState("");
  const [variables, setVariables] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Extract variables from content when it changes
  useEffect(() => {
    const extractedVariables = extractVariablesFromContent(content);
    if (extractedVariables.length > 0) {
      // Only add variables that aren't already in the list
      const newVariables = extractedVariables.filter(v => !variables.includes(v));
      if (newVariables.length > 0) {
        setVariables(prev => [...prev, ...newVariables]);
      }
    }
  }, [content, variables]);

  // Function to extract variables from text content (format: {{variable}})
  const extractVariablesFromContent = (text: string): string[] => {
    const regex = /\{\{([^{}]+)\}\}/g;
    const matches = [];
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      if (!matches.includes(match[1])) {
        matches.push(match[1]);
      }
    }
    
    return matches;
  };

  const handleAddVariable = () => {
    if (!variableInput.trim()) return;
    
    // Prevent duplicates
    if (!variables.includes(variableInput)) {
      setVariables([...variables, variableInput]);
      setVariableInput("");
    } else {
      toast({
        title: "Duplicate Variable",
        description: "This variable is already added.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveVariable = (variable: string) => {
    setVariables(variables.filter(v => v !== variable));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddVariable();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Please enter a template name",
        variant: "destructive",
      });
      return;
    }
    
    if (!content.trim()) {
      toast({
        title: "Error",
        description: "Please enter the template content",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const templateData: Omit<Template, 'id' | 'createdAt' | 'updatedAt'> = {
        name,
        content,
        variables
      };
      
      await createTemplate(templateData);
      
      toast({
        title: "Success",
        description: "Template created successfully",
      });
      
      // Reset form
      setName("");
      setContent("");
      setVariables([]);
      
      // Navigate to templates list
      navigate("/whatsapp");
    } catch (error) {
      console.error('Error creating template:', error);
      toast({
        title: "Error",
        description: "Failed to create template",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Template Name</Label>
            <Input
              id="name"
              placeholder="Enter a name for your template"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="content">Message Template</Label>
            <Textarea
              id="content"
              placeholder="Type your message template here. Use {{variable}} for dynamic content."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[120px]"
            />
            <p className="text-sm text-gray-500">
              Use {"{{"} variable {"}}"}  format to add dynamic variables, such as {"{{"} name {"}}"},  {"{{"} date {"}}"},  etc.
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="variables">Variables</Label>
            <div className="flex gap-2">
              <Input
                id="variables"
                placeholder="Add a variable (without {{ }})"
                value={variableInput}
                onChange={(e) => setVariableInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button type="button" onClick={handleAddVariable} variant="outline">
                Add
              </Button>
            </div>
            
            {variables.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {variables.map((variable) => (
                  <Badge key={variable} variant="secondary" className="flex items-center gap-1">
                    {variable}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => handleRemoveVariable(variable)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Template"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
